// Serviço de cache para requisições API com IndexedDB
// Mais robusto que localStorage, suporta grandes volumes de dados

const DB_NAME = 'MX3_API_CACHE';
const DB_VERSION = 1;
const STORE_NAME = 'api_responses';

class ApiCache {
  constructor() {
    this.cache = new Map(); // Cache em memória para acesso rápido
    this.pendingRequests = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos
    this.db = null;
    this.dbReady = this.initDB();
  }

  // Inicializa IndexedDB
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.warn('[Cache] Erro ao abrir IndexedDB');
        resolve(null);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        console.log('[Cache] IndexedDB inicializado com sucesso');
        this.loadFromIndexedDB().then(() => resolve(this.db));
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  // Carrega cache do IndexedDB para memória (apenas metadados, não dados pesados)
  async loadFromIndexedDB() {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = (event) => {
        const entries = event.target.result;
        let loadedCount = 0;

        entries.forEach((entry) => {
          // Verifica se ainda é válido antes de carregar
          if (Date.now() - entry.timestamp < entry.ttl) {
            // Carrega apenas os dados essenciais para o cache em memória
            this.cache.set(entry.key, {
              data: entry.data,
              timestamp: entry.timestamp,
              ttl: entry.ttl,
              isFromDB: true
            });
            loadedCount++;
          } else {
            // Remove entradas expiradas
            this.removeFromIndexedDB(entry.key);
          }
        });

        console.log(`[Cache] Carregado ${loadedCount} entradas do IndexedDB`);
      };
    } catch (error) {
      console.warn('[Cache] Erro ao carregar do IndexedDB:', error);
    }
  }

  // Salva no IndexedDB (assíncrono, não bloqueia)
  async saveToIndexedDB(key, data, timestamp, ttl) {
    if (!this.db) return;

    try {
      // Limita o tamanho dos dados armazenados
      const dataSize = JSON.stringify(data).length;
      const maxSize = 1024 * 1024; // 1MB por entrada

      if (dataSize > maxSize) {
        console.warn(`[Cache] Dados muito grandes (${dataSize} bytes), não serão persistidos: ${key}`);
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      // Clona o objeto para garantir que não contenha funções ou elementos não clonáveis (IndexedDB requirement)
      // Se falhar ao clonar, apenas o cache em memória funcionará para esta entrada
      let cloneableData;
      try {
        cloneableData = JSON.parse(JSON.stringify(data));
      } catch (e) {
        console.warn(`[Cache] Dados para ${key} contêm objetos não clonáveis, salvando apenas em memória.`);
        return;
      }

      await store.put({
        key,
        data: cloneableData,
        timestamp,
        ttl
      });
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('[Cache] Limite de espaço atingido, limpando entradas antigas...');
        await this.cleanOldestEntries();
      } else {
        console.warn('[Cache] Erro ao salvar no IndexedDB:', error);
      }
    }
  }

  // Remove do IndexedDB
  async removeFromIndexedDB(key) {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      await store.delete(key);
    } catch (error) {
      console.warn('[Cache] Erro ao remover do IndexedDB:', error);
    }
  }

  // Limpa as entradas mais antigas quando atinge limite
  async cleanOldestEntries() {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');

      // Pega as 10 entradas mais antigas
      const request = index.openCursor();
      let count = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor && count < 10) {
          store.delete(cursor.value.key);
          this.cache.delete(cursor.value.key);
          count++;
          cursor.continue();
        }
      };
    } catch (error) {
      console.warn('[Cache] Erro ao limpar entradas antigas:', error);
    }
  }

  // Gera uma chave única baseada na URL e parâmetros
  generateKey(url, params = {}) {
    const paramsKey = Object.keys(params).length > 0 
      ? JSON.stringify(params) 
      : '';
    return `${url}:${paramsKey}`;
  }

  // Verifica se o cache ainda é válido
  isValid(cacheEntry) {
    return cacheEntry && Date.now() - cacheEntry.timestamp < cacheEntry.ttl;
  }

  // Busca dados do cache
  get(url, params = {}) {
    const key = this.generateKey(url, params);
    const cacheEntry = this.cache.get(key);
    
    if (this.isValid(cacheEntry)) {
      console.log(`[Cache HIT] ${url}`);
      return Promise.resolve(cacheEntry.data);
    }
    
    // Remove entrada expirada
    if (cacheEntry) {
      this.cache.delete(key);
      this.removeFromIndexedDB(key);
    }
    
    return null;
  }

  // Armazena dados no cache
  set(url, params = {}, data, ttl = this.defaultTTL) {
    const key = this.generateKey(url, params);
    const timestamp = Date.now();

    this.cache.set(key, {
      data,
      timestamp,
      ttl
    });

    console.log(`[Cache SET] ${url}`);
    
    // Persiste no IndexedDB de forma assíncrona (não bloqueia)
    this.saveToIndexedDB(key, data, timestamp, ttl);
  }

  // Executa uma requisição com cache
  async fetch(url, fetchFn, params = {}, ttl = this.defaultTTL) {
    await this.dbReady; // Aguarda IndexedDB estar pronto

    const key = this.generateKey(url, params);
    
    // Verifica se já existe uma requisição pendente
    if (this.pendingRequests.has(key)) {
      console.log(`[Cache PENDING] ${url} - aguardando requisição em andamento`);
      return this.pendingRequests.get(key);
    }
    
    // Tenta buscar do cache primeiro
    const cached = this.get(url, params);
    if (cached) {
      return cached;
    }
    
    // Cria a promessa da requisição
    const promise = fetchFn()
      .then(response => {
        this.set(url, params, response, ttl);
        this.pendingRequests.delete(key);
        return response;
      })
      .catch(error => {
        this.pendingRequests.delete(key);
        throw error;
      });
    
    // Armazena a promessa para evitar chamadas duplicadas simultâneas
    this.pendingRequests.set(key, promise);
    
    return promise;
  }

  // Limpa todo o cache ou uma entrada específica
  async clear(url, params = {}) {
    if (url) {
      const key = this.generateKey(url, params);
      this.cache.delete(key);
      await this.removeFromIndexedDB(key);
    } else {
      this.cache.clear();
      if (this.db) {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await store.clear();
      }
    }
  }

  // Limpa entradas expiradas
  async cleanExpired() {
    const now = Date.now();
    let removedCount = 0;
    const keysToRemove = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        keysToRemove.push(key);
        removedCount++;
      }
    }

    for (const key of keysToRemove) {
      this.cache.delete(key);
      await this.removeFromIndexedDB(key);
    }

    if (removedCount > 0) {
      console.log(`[Cache] Removidas ${removedCount} entradas expiradas`);
    }
  }

  // Retorna estatísticas do cache
  getStats() {
    let totalSize = 0;
    this.cache.forEach((value) => {
      totalSize += JSON.stringify(value.data).length;
    });

    return {
      entries: this.cache.size,
      totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
      averageSize: this.cache.size > 0 ? `${(totalSize / this.cache.size / 1024).toFixed(2)} KB` : '0 KB'
    };
  }
}

// Exporta instância singleton
export const apiCache = new ApiCache();

// Helper para uso em componentes Vue
export function useApiCache() {
  return apiCache;
}
